import { ArrowRightIcon } from "lucide-react"
import type * as React from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { useAuth } from "@/features/auth/auth-context"
import { Badge } from "@/components/ui/badge"
import {
  Card,
  CardAction,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import type { DashboardSummaryResponse } from "@/lib/endpoints"
import { formatRupees } from "@/lib/money"
import { routes } from "@/routes/paths"

/**
 * "Unpaid bills: 7 — ₹3,42,150" — §17.5 on the first screen he sees, and the
 * closing of §11.2's known gap.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * §11.2 CALLED THIS THE SINGLE MOST USEFUL FIGURE A BUSINESS OWNER CAN HAVE, and
 * then forbade it: "until payment tracking exists the at-a-glance strip must not
 * imply anything about payment status". §17.5 now exists — a ledger, and an
 * outstanding figure derived from it on every read — so the strip may finally say
 * it, on exactly the terms the ban implied.
 *
 * PLAIN LANGUAGE, NOT ACCOUNTING (§11.2). "Unpaid bills", never "Receivables
 * MTD", "Debtors" or "Ageing". The owner is a fabricator, not an analyst, and a
 * word he has to translate before he can act on it is a word that has cost him
 * the tap.
 *
 * AND NEVER "OVERDUE". This system models no credit terms and stores no due
 * date, so calling a forty-day-old bill late would be the app inventing an
 * agreement the business never made. The API sends `ageDays` for the register
 * and deliberately does not name it overdue either.
 *
 * ABSENT RATHER THAN ZERO, on all three of the reasons that can produce nothing:
 *
 *   - THE MODULE IS OFF. `GET /dashboard/summary` is deliberately not flagged
 *     (§18.3 forbids flagging the home screen) while every payment route is, so
 *     the API OMITS the key rather than sending 0. Here the zero would be worse
 *     than the leak: "Unpaid bills: 0" is a positive claim that every bill has
 *     been settled, made by a build that is not recording payments at all.
 *   - THE CALLER LACKS `payment.view`. Same omission, server-side. The `can()`
 *     check below is the client half, so this cannot link somewhere the tap
 *     would 404 — and it is a CAPABILITY, never a role (§2.1), so the day the
 *     ACCOUNTANT row is switched on this stays honest by itself.
 *   - GENUINELY NOTHING IS OWED. The card is a to-do list; one that is
 *     permanently on screen saying "nothing to chase" is what the eye learns to
 *     skip, and then the week it says ₹3,42,150 it gets skipped too.
 *
 * Undefined and zero therefore render the same nothing, and neither needs a
 * branch of its own — the same rule `UnbilledDeliveries` follows, so the strip
 * has one rule rather than two.
 *
 * A COUNT *AND* A RUPEE FIGURE, unlike §17.7's count. That is the whole point of
 * §17.5 landing: "Unpaid bills: 7" is the question without the answer, and
 * §11.2's own wording for the closed gap is "who owes me money". The money is
 * the figure, the count is the badge beside it — the same grammar the "Billed
 * this month" card already uses.
 *
 * IT IS NOT A CHART AND MUST NOT BECOME ONE. §11.2 allows exactly one chart and
 * `monthlyBilled` is it.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function UnpaidBills({
  summary,
}: {
  summary: DashboardSummaryResponse | undefined
}): React.JSX.Element | null {
  const { t } = useTranslation()
  const { can } = useAuth()

  /*
   * NO SKELETON WHILE IT LOADS, for the same reason `UnbilledDeliveries` has
   * none: the three cards above carry the loading state for this row, and a
   * fourth skeleton that resolves to nothing on most days is a placeholder for
   * an absence.
   */
  const unpaid = summary?.unpaidBills
  if (unpaid === undefined || unpaid.count <= 0 || !can("payment.view")) return null

  return (
    <div className="*:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card grid grid-cols-1 gap-4 px-4 *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:shadow-xs lg:px-6">
      <Card className="@container/card">
        <CardHeader>
          <CardDescription>{t("home.card.unpaidBills")}</CardDescription>
          <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
            {/*
              * `formatRupees`, never `toLocaleString` — Indian digit grouping is
              * ₹3,42,150.00 and a runtime built with small-icu falls back to
              * western grouping silently (CLAUDE.md). And `formatRupees` rather
              * than the bare `formatPaise`, because a card whose whole content is
              * one figure needs the ₹ on it.
              */}
            {formatRupees(unpaid.totalPaise)}
          </CardTitle>
          <CardAction>
            <Badge variant="outline">{t("home.card.bills", { count: unpaid.count })}</Badge>
          </CardAction>
        </CardHeader>
        <CardFooter className="flex-col items-start gap-1.5 text-sm">
          {/*
            * `routes.invoicesUnpaid` — the register OPENS ALREADY FILTERED, on
            * the same `readOutstandingInvoices` rows that produced this total.
            * A link to the unfiltered register would show 214 bills to someone
            * who was just told there were 7, and a figure whose list disagrees
            * with it stops being believed within a week (§17.7 paid for that
            * lesson already).
            */}
          <Link
            to={routes.invoicesUnpaid}
            className="line-clamp-1 flex gap-2 font-medium hover:underline"
          >
            {t("home.card.openUnpaid")} <ArrowRightIcon className="size-4" />
          </Link>
          <div className="text-muted-foreground">{t("home.card.unpaidHelp")}</div>
        </CardFooter>
      </Card>
    </div>
  )
}
